import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material";
import React, { FC } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ILoginActivity } from "../../../models/admin/loginActivity/loginActivity";
import { useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";

interface ActivityCount {
  login: number;
  logout: number;
  failed: number;
}
interface IDailyActivityTrendsProps {
  filteredData: ILoginActivity[];
}

const processData = (data: ILoginActivity[]) => {
  const result: Record<string, ActivityCount> = {};

  data?.forEach((entry) => {
    const date = entry.loginTime
      ? new Date(entry.loginTime).toLocaleDateString()
      : null;

    if (!date) return;

    if (!result[date]) {
      result[date] = { login: 0, logout: 0, failed: 0 };
    }

    if (entry.comments === "SUCCESSFUL_LOGIN" && entry.loginTime) {
      result[date].login += 1;
    } else if (entry.comments === "LOGOUT_SUCCESS" && entry.logoutTime) {
      result[date].logout += 1;
    } else if (entry.activity?.toLowerCase() === "failed") {
      result[date].failed += 1;
    } else if (entry.activity?.toLowerCase() && !entry.logoutTime) {
      result[date].login += 1;
    }
  });

  // Sort the dates in descending order to get the latest dates first
  return Object.keys(result)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    .map((date) => ({
      date,
      Login: result[date].login,
      Logout: result[date].logout,
      Failed: result[date].failed,
    }))
    .slice(0, 6); // Take the first 6 entries after sorting
};

const COLORS = ["#4CAF50", "#FF9800", "#F44336"];

const DailyActivityTrends: FC<IDailyActivityTrendsProps> = ({
  filteredData = [],
}) => {
  console.log("filteredData:", filteredData);
  const isMobile = useMediaQuery("(max-width:767px)");
  const { isLoading } = useAppSelector((store: RootState) => store.common);
  const data = processData(filteredData);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "white",
        padding: 2,
        borderRadius: 2,
        boxShadow: 1,
      }}
      id="daily_activity_trends_container"
      data-test-id="daily_activity_trends_container"
      aria-label="Daily Activity Trends Chart"
    >
      <Typography 
        sx={{ fontWeight: "600", marginBottom: 2 }}
        id="daily_activity_trends_title"
        data-test-id="daily_activity_trends_title"
        aria-label="Daily Activity Trends"
      >
        Daily Activity Trends
      </Typography>
      {isLoading ? (
        <Skeleton
          variant="rounded"
          height="250px"
          animation="wave"
          sx={{
            width: isMobile ? "100%" : "auto",
            minWidth: isMobile ? "50vw" : "466px",
            flex: 1,
          }}
          id="daily_activity_trends_skeleton"
          data-test-id="daily_activity_trends_skeleton"
          aria-label="Loading Daily Activity Trends"
        />
      ) : (
        <Box 
          sx={{ height: 300 }}
          id="daily_activity_trends_chart"
          data-test-id="daily_activity_trends_chart"
          aria-label="Daily Activity Trends Chart Content"
        >
          {data?.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
              id="daily_activity_trends_empty"
              data-test-id="daily_activity_trends_empty"
              aria-label="No Daily Activity Records Found"
            >
              <Typography
                sx={{ textAlign: "center", fontSize: 16, color: "black" }}
                id="daily_activity_trends_empty_title"
                data-test-id="daily_activity_trends_empty_title"
                aria-label="No Daily Activity Records Found"
              >
                No records found
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date"
                  id="daily_activity_trends_xaxis"
                  data-test-id="daily_activity_trends_xaxis"
                  aria-label="Date Axis"
                />
                <YAxis 
                  id="daily_activity_trends_yaxis"
                  data-test-id="daily_activity_trends_yaxis"
                  aria-label="Activity Count Axis"
                />
                <Tooltip />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                  iconType="circle"
                  iconSize={12}
                  wrapperStyle={{
                    paddingTop: "10px",
                  }}
                  id="daily_activity_trends_legend"
                  data-test-id="daily_activity_trends_legend"
                  aria-label="Daily Activity Trends Legend"
                />
                <Bar 
                  dataKey="Login" 
                  fill={COLORS[0]} 
                  barSize={20}
                  id="daily_activity_trends_login_bar"
                  data-test-id="daily_activity_trends_login_bar"
                  aria-label="Login Activity Bar"
                />
                <Bar 
                  dataKey="Logout" 
                  fill={COLORS[1]} 
                  barSize={20}
                  id="daily_activity_trends_logout_bar"
                  data-test-id="daily_activity_trends_logout_bar"
                  aria-label="Logout Activity Bar"
                />
                <Bar 
                  dataKey="Failed" 
                  fill={COLORS[2]} 
                  barSize={20}
                  id="daily_activity_trends_failed_bar"
                  data-test-id="daily_activity_trends_failed_bar"
                  aria-label="Failed Activity Bar"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DailyActivityTrends;
